import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Search,
  Calendar,
  User,
  Phone,
  MapPin,
  Clock,
  RefreshCw,
  Check,
  ChevronLeft,
  ChevronRight,
  Crown,
  Receipt,
  Loader,
  Plus,
  Globe,
  Mail,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import RedemptionAnalytics from "../../components/RedemptionAnalytics";
import axios from "axios";
import { toast } from "react-toastify";
import Config from "../../Config";
import LocationModal from "../../utils/LocationModal";
import { Button } from "@mui/material";
import moment from "moment";
import { formatDate } from "../../utils/Utils";

const PremiumDealRedemptions = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [recordsPerPage] = useState(10);
  const [redemptions, setRedemptions] = useState([]);
  const [totalRedemptions, setTotalRedemptions] = useState(0);
  const [isLoadingRedemptions, setIsLoadingRedemptions] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortConfig, setSortConfig] = useState({
    key: "redeemedAt",
    direction: "desc",
  });
  const [allRedemptionsData, setAllRedemptionsData] = useState([]);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemingId, setRedeemingId] = useState(null);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  // Fetch deal info
  useEffect(() => {
    const fetchDealInfo = async () => {
      try {
        const response = await axios.get(`${Config.baseUrl}/only/deals/${id}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
          },
        });
        const data = await response?.data;
        if (data?.data?.status_code === "000") {
          setLoading(false);
          setDeal(data?.data?.data);
        } else {
          toast.error(
            data?.data?.reason || "Sorry, the deal could not be found"
          );
        }
      } catch (error) {
        setLoading(false);
        toast.error(
          error?.response?.data?.reason || "Sorry, the deal could not be found"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchDealInfo();
  }, [id]);

  // Fetch redemptions with filters and pagination
  useEffect(() => {
    const fetchRedemptions = async () => {
      if (!deal) return;

      setIsLoadingRedemptions(true);
      try {
        const response = await axios.get(
          `${Config.baseUrl}/redeemed/deal/${deal.premium_deal_slug}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
            },
          }
        );
        const data = await response?.data;
        
        if (data?.data?.status_code === "000") {
          let filteredRedemptions = [...data.data.data];
          if (searchTerm) {
            filteredRedemptions = filteredRedemptions.filter(
              (r) =>
                r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                r.phone_number?.includes(searchTerm) ||
                r.redemption_code?.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          if (filterStatus !== "all") {
            filteredRedemptions = filteredRedemptions.filter(
              (r) => r.status === filterStatus
            );
          }
          if (dateRange.start) {
            const startDate = new Date(dateRange.start);
            filteredRedemptions = filteredRedemptions.filter(
              (r) => new Date(r.purchasedAt) >= startDate
            );
          }

          if (dateRange.end) {
            const endDate = new Date(dateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filteredRedemptions = filteredRedemptions.filter(
              (r) => new Date(r.purchasedAt) <= endDate
            );
          }
          filteredRedemptions.sort((a, b) => {
            if (sortConfig.key === "redeemedAt") {
              if (!a.redeemedAt && !b.redeemedAt) return 0;
              if (!a.redeemedAt) return sortConfig.direction === "asc" ? 1 : -1;
              if (!b.redeemedAt) return sortConfig.direction === "asc" ? -1 : 1;

              return sortConfig.direction === "asc"
                ? new Date(a.redeemedAt).getTime() - new Date(b.redeemedAt).getTime()
                : new Date(b.redeemedAt).getTime() - new Date(a.redeemedAt).getTime();
            }

            if (sortConfig.key === "purchasedAt") {
              return sortConfig.direction === "asc"
                ? new Date(a.purchasedAt).getTime() - new Date(b.purchasedAt).getTime()
                : new Date(b.purchasedAt).getTime() - new Date(a.purchasedAt).getTime();
            }

            if (sortConfig.key === "customerName") {
              return sortConfig.direction === "asc"
                ? (a.customerName || "").localeCompare(b.customerName || "")
                : (b.customerName || "").localeCompare(a.customerName || "");
            }

            return 0;
          });

          // Store total filtered results
          setTotalRedemptions(filteredRedemptions.length);
          setAllRedemptionsData(filteredRedemptions);

          // Apply pagination
          const startIndex = (currentPage - 1) * recordsPerPage;
          const paginatedRedemptions = filteredRedemptions.slice(
            startIndex,
            startIndex + recordsPerPage
          );

          setRedemptions(paginatedRedemptions);
          setTotalPages(Math.ceil(filteredRedemptions.length / recordsPerPage));
        } else {
          toast.error(data?.data?.reason || "Sorry, the deal could not be found");
        }
      } catch (error) {
        console.error("Error fetching redemptions:", error);
        toast.error(error?.response?.data?.reason || "Failed to fetch redemptions");
      } finally {
        setIsLoadingRedemptions(false);
      }
    };

    fetchRedemptions();
  }, [
    deal,
    currentPage,
    recordsPerPage,
    searchTerm,
    filterStatus,
    dateRange,
    sortConfig,
  ]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  const handleDateChange = (type, value) => {
    setDateRange((prev) => ({ ...prev, [type]: value }));
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key
          ? prevConfig.direction === "asc"
            ? "desc"
            : "asc"
          : "asc",
    }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleMarkAsRedeemed = async (redemptionId) => {
    setRedeeming(true);
    setRedeemingId(redemptionId);

    try {


      // Simulating API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Update local state
      setRedemptions((prev) =>
        prev.map((r) =>
          r.id === redemptionId
            ? {
                ...r,
                status: "verified",
                redeemedAt: new Date(),
                redeemedLocation: "Marked by admin",
              }
            : r
        )
      );

      // Update analytics data
      setAllRedemptionsData((prev) =>
        prev.map((r) =>
          r.id === redemptionId
            ? {
                ...r,
                status: "verified",
                redeemedAt: new Date(),
                redeemedLocation: "Marked by admin",
              }
            : r
        )
      );
      alert("Redemption code marked as redeemed successfully!");
    } catch (err) {
      console.error("Error marking redemption as redeemed:", err);
      alert("Failed to mark redemption as redeemed. Please try again.");
    } finally {
      setRedeeming(false);
      setRedeemingId(null);
    }
  };

  const redemptionStats = useMemo(() => {
    if (!totalRedemptions)
      return { redeemed: 0, pending: 0, redeemedPercentage: 0 };

    const redeemed = redemptions.filter((r) => r.status === "verified").length;
    const pending = redemptions.filter((r) => r.status === "redeemed").length;
    const redeemedPercentage = Math.round(
      (redeemed / (redeemed + pending)) * 100
    );

    return { redeemed, pending, redeemedPercentage };
  }, [redemptions, totalRedemptions]);

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar page="Premium Deal Redemptions" />

      <LocationModal
        open={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        locations={deal?.location || []}
      />
      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-between items-center mb-6">
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate(`/premium-deals/${id}`)}
            className="flex gap-2 items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Deal</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-2 items-center"
          >
            <div className="p-2 bg-indigo-100 rounded-full">
              <Receipt className="w-5 h-5 text-indigo-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-800">Redemptions</h1>
          </motion.div>
        </div>

        {/* Deal summary card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 mb-6 bg-white rounded-xl shadow-sm sm:p-6"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
            <div className="flex-1 space-y-3">
              <div className="flex gap-2 items-center">
                <Crown className="w-5 h-5 text-amber-600 shrink-0" />
                <h2 className="text-lg font-semibold text-gray-900 break-words">
                  {deal.title}
                </h2>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
                <div className="flex gap-2 items-start sm:items-center">
                  <MapPin className="mt-0.5 w-4 h-4 text-gray-400 shrink-0 sm:mt-0" />
                  <span className="text-sm text-gray-600">
                    {deal.location && deal.location.length > 0 ? (
                      deal.location[0].region === "all" ? (
                        <div className="flex gap-2 items-center">
                          <Globe className="w-4 h-4 shrink-0" />
                          <span>Available Nationwide</span>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2 items-center">
                          <span className="break-words">{`${deal.location[0].location}, ${deal.location[0].region} Region`}</span>
                          {deal.location.length > 1 && (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => setIsLocationModalOpen(true)}
                              startIcon={<Plus className="w-3 h-3" />}
                              sx={{
                                minWidth: "auto",
                                padding: "2px 8px",
                                color: "#F59E0B",
                                textTransform: "none",
                                "&:hover": {
                                  backgroundColor: "#FEF3C7",
                                },
                              }}
                            >
                              {deal.location.length - 1} more
                            </Button>
                          )}
                        </div>
                      )
                    ) : (
                      "Location not specified"
                    )}
                  </span>
                </div>
                <div className="flex gap-1 items-center text-sm text-gray-500">
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span className="whitespace-nowrap">
                    Valid until {moment(deal?.end_date).format("DD-MMM-YYYY")}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 text-xl font-bold text-amber-600 sm:text-right">
              ₵{deal.discounted_price}
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 mb-6 bg-white rounded-xl shadow-sm sm:p-6"
        >
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            {/* Search input */}
            <div className="relative lg:col-span-1">
              <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search by email, phone, or code..."
                value={searchTerm}
                onChange={handleSearch}
                className="py-2 pr-4 pl-10 w-full bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Status filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter("all")}
                className={`px-3 py-2 text-sm font-medium rounded-lg ${
                  filterStatus === "all"
                    ? "bg-indigo-100 text-indigo-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleStatusFilter("verified")}
                className={`flex gap-1 items-center px-3 py-2 text-sm font-medium rounded-lg ${
                  filterStatus === "verified"
                    ? "bg-green-100 text-green-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Check className="w-4 h-4" />
                Redeemed
              </button>
              <button
                onClick={() => handleStatusFilter("redeemed")}
                className={`flex gap-1 items-center px-3 py-2 text-sm font-medium rounded-lg ${
                  filterStatus === "redeemed"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <Clock className="w-4 h-4" />
                Pending
              </button>
            </div>

            {/* Date range filter */}
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="sr-only">From</label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => handleDateChange("start", e.target.value)}
                  className="p-2 w-full rounded-lg border border-gray-200 focus:ring-2 focus:outline-none focus:ring-indigo-500"
                />
              </div>
              <div className="flex-1">
                <label className="sr-only">To</label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => handleDateChange("end", e.target.value)}
                  className="p-2 w-full rounded-lg border border-gray-200 focus:ring-2 focus:outline-none focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Stats and export */}
          <div className="flex flex-wrap gap-4 justify-between items-center mt-6">
            <div className="flex flex-wrap gap-y-2 gap-x-6">
              <div>
                <span className="text-sm text-gray-500">Total:</span>{" "}
                <span className="font-medium">{totalRedemptions}</span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Redeemed:</span>{" "}
                <span className="font-medium text-green-600">
                  {redemptionStats.redeemed}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Pending:</span>{" "}
                <span className="font-medium text-amber-600">
                  {redemptionStats.pending}
                </span>
              </div>
              <div>
                <span className="text-sm text-gray-500">Redemption Rate:</span>{" "}
                <span className="font-medium text-indigo-600">
                  {redemptionStats.redeemedPercentage}%
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => window.location.reload()}
                className="flex gap-1 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </motion.div>

        {/* Redemptions table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden bg-white rounded-xl shadow-sm"
        >
          {isLoadingRedemptions ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-12 h-12 rounded-full border-b-2 border-indigo-600 animate-spin"></div>
            </div>
          ) : redemptions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full table-auto">
                <thead className="text-xs font-medium text-left text-gray-500 uppercase bg-gray-50">
                  <tr>
                    <th className="px-6 py-3">
                      <button
                        onClick={() => handleSort("customerName")}
                        className="flex gap-1 items-center"
                      >
                        Customer
                        {sortConfig.key === "customerName" && (
                          <span>
                            {sortConfig.direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3">Phone Number</th>
                    <th className="px-6 py-3">Code</th>

                    <th className="px-6 py-3">
                      <button
                        onClick={() => handleSort("redeemedAt")}
                        className="flex gap-1 items-center"
                      >
                        REDEEMED AT
                        {sortConfig.key === "redeemedAt" && (
                          <span>
                            {sortConfig.direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </button>
                    </th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm divide-y divide-gray-200">
                  {redemptions.map((redemption) => (
                    <tr key={redemption.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex gap-3 items-center">
                          <div className="p-2 bg-indigo-50 rounded-full">
                            <Mail className="w-4 h-4 text-indigo-500" />
                          </div>
                          <span>{redemption.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2 items-center">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <span>{redemption.phone_number}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2.5 py-1 font-mono text-xs font-medium text-indigo-700 bg-indigo-50 rounded-lg">
                          {redemption.redemption_code}
                        </span>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {redemption.status!=="redeemed"
                          ? moment().format("DD-MMM-YYYY")
                          : "-"}
                      </td>

                      <td className="px-6 py-4">
                        {redemption.status === "redeemed" ? (
                          <span className="flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        ) : redemption.status === "verified" ? (
                          <span className="flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 rounded-full">
                            <Check className="w-3 h-3" />
                            Redeemed
                          </span>
                        ) : (
                          <span className="flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full">
                            <Clock className="w-3 h-3" />
                            Pending
                          </span>
                        )}
                      </td>
                     
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center py-12">
              <Receipt className="mb-4 w-12 h-12 text-gray-300" />
              <h3 className="mb-2 text-lg font-medium text-gray-800">
                No redemptions found
              </h3>
              <p className="text-gray-500">
                Try adjusting your filters or search criteria.
              </p>
            </div>
          )}

          {/* Pagination controls */}
          {totalPages > 1 && (
            <div className="flex justify-center py-4 border-t border-gray-200">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border ${
                    currentPage === 1
                      ? "text-gray-400 border-gray-200 cursor-not-allowed"
                      : "text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Logic for showing appropriate page numbers
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`flex justify-center items-center w-10 h-10 text-sm border rounded-lg ${
                          currentPage === pageNum
                            ? "text-white bg-indigo-600 border-indigo-600"
                            : "text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {totalPages > 5 && currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(totalPages)}
                        className="flex justify-center items-center w-10 h-10 text-sm text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border ${
                    currentPage === totalPages
                      ? "text-gray-400 border-gray-200 cursor-not-allowed"
                      : "text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PremiumDealRedemptions;
